import { useEffect, useState } from "react";
import { useParams, Link } from "wouter";
import {
  Container,
  Title,
  Card,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  LoadingOverlay,
  Breadcrumbs,
  Anchor,
  Divider,
  Paper,
  Alert,
} from "@mantine/core";
import {
  IconSend,
  IconExternalLink,
  IconFileText,
  IconSparkles,
} from "@tabler/icons-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useContentPlansStore } from "./contentPlansStore";
import { getContentItem } from "../Content/contentApi";
import { modals } from "@mantine/modals";

const statusColors = {
  draft: "blue",
  readyForReview: "yellow",
  published: "teal",
  archived: "gray",
};

export default function ClusterReview() {
  const { planId, workstreamId } = useParams();
  const { currentPlan, isLoading, fetchContentPlan, publishWorkstream } =
    useContentPlansStore();
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const workstream = currentPlan?.workstreams?.find(
    (ws) => ws.id === workstreamId
  );

  // Load the plan if not already loaded
  useEffect(() => {
    if (!currentPlan || currentPlan.id !== planId) {
      fetchContentPlan(planId);
    }
  }, [planId, currentPlan, fetchContentPlan]);

  // Load all article content for this workstream
  useEffect(() => {
    if (!workstream) return;

    const ideasWithContent = workstream.contentIdeas?.filter(
      (i) => i.contentDocumentId
    );
    if (!ideasWithContent?.length) {
      setLoadingArticles(false);
      return;
    }

    setLoadingArticles(true);
    Promise.all(
      ideasWithContent.map(async (idea) => {
        try {
          const item = await getContentItem(idea.contentDocumentId);
          return { idea, item };
        } catch {
          return { idea, item: null };
        }
      })
    ).then((results) => {
      setArticles(results.filter((r) => r.item));
      setLoadingArticles(false);
    });
  }, [workstream]);

  const readyToPublish = articles.filter(
    (a) => a.item.status !== "Published"
  );
  const reviewedReady = articles.filter(
    (a) => a.item.status === "ReadyForReview" && a.item.reviewedAt
  );

  const handlePublish = () => {
    modals.openConfirmModal({
      title: "Publish entire workstream",
      children: (
        <Text size="sm">
          Publish {reviewedReady.length} reviewed draft
          {reviewedReady.length !== 1 ? "s" : ""} in this cluster.
          Unreviewed articles are skipped. Cross-links between published
          articles stay intact.
        </Text>
      ),
      labels: { confirm: "Publish all drafts", cancel: "Cancel" },
      confirmProps: { color: "green" },
      onConfirm: async () => {
        setPublishing(true);
        await publishWorkstream(planId, workstreamId);
        setPublishing(false);
        // Reload articles to get updated statuses
        if (workstream) {
          const ideasWithContent = workstream.contentIdeas?.filter(
            (i) => i.contentDocumentId
          );
          if (ideasWithContent?.length) {
            const results = await Promise.all(
              ideasWithContent.map(async (idea) => {
                try {
                  const item = await getContentItem(idea.contentDocumentId);
                  return { idea, item };
                } catch {
                  return { idea, item: null };
                }
              })
            );
            setArticles(results.filter((r) => r.item));
          }
        }
      },
    });
  };

  if (isLoading && !currentPlan) {
    return (
      <Container size="lg" py="xl">
        <LoadingOverlay visible />
      </Container>
    );
  }

  if (!workstream) {
    return (
      <Container size="lg" py="xl">
        <Text>Workstream not found</Text>
      </Container>
    );
  }

  const publishedCount =
    workstream.contentIdeas?.filter((i) => i.status === "published").length || 0;
  const draftCount = articles.filter(
    (a) => a.item.status !== "Published"
  ).length;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} href="/plans">
            Clusters
          </Anchor>
          <Anchor component={Link} href={`/plans/${planId}`}>
            {currentPlan?.name || "Plan"}
          </Anchor>
          <Anchor
            component={Link}
            href={`/plans/${planId}/clusters/${workstreamId}`}
          >
            {workstream.title}
          </Anchor>
          <Text>Review drafts</Text>
        </Breadcrumbs>

        {/* Header */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between">
            <div>
              <Title order={2}>{workstream.title}</Title>
              <Text size="sm" c="dimmed" mt="xs">
                {workstream.goal}
              </Text>
            </div>
            <Stack gap="xs" align="flex-end">
              <Group gap="xs">
                <Badge color="teal" variant="light">
                  {publishedCount} published
                </Badge>
                <Badge color="blue" variant="light">
                  {draftCount} draft
                </Badge>
              </Group>
              {readyToPublish.length > 0 && (
                <>
                  <Button
                    variant="default"
                    size="xs"
                    leftSection={<IconSend size={14} />}
                    loading={publishing}
                    disabled={reviewedReady.length === 0}
                    onClick={handlePublish}
                  >
                    Publish all {reviewedReady.length} reviewed
                  </Button>
                  {reviewedReady.length === 0 && (
                    <Text size="xs" c="dimmed">
                      Review each draft in its Claims tab first.
                    </Text>
                  )}
                </>
              )}
            </Stack>
          </Group>
        </Card>

        {loadingArticles ? (
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <LoadingOverlay visible />
            <div style={{ height: 200 }} />
          </Card>
        ) : articles.length === 0 ? (
          <Alert color="yellow" title="No articles to review">
            No articles have been generated in this cluster yet.
          </Alert>
        ) : (
          <>
            {/* Article list */}
            {articles
              .sort((a, b) => {
                // Pillars first
                const aIsPillar =
                  a.idea.contentRole?.toLowerCase() === "pillar";
                const bIsPillar =
                  b.idea.contentRole?.toLowerCase() === "pillar";
                if (aIsPillar && !bIsPillar) return -1;
                if (!aIsPillar && bIsPillar) return 1;
                return 0;
              })
              .map(({ idea, item }, index) => (
                <Paper
                  key={item.id}
                  p="xl"
                  withBorder
                  radius="md"
                  shadow="sm"
                >
                  <Stack gap="md">
                    {/* Article header */}
                    <Group justify="space-between">
                      <Group gap="sm">
                        {idea.contentRole?.toLowerCase() === "pillar" ? (
                          <Badge
                            leftSection={<IconSparkles size={12} />}
                            color="violet"
                            variant="light"
                          >
                            Pillar
                          </Badge>
                        ) : (
                          <Badge
                            leftSection={<IconFileText size={12} />}
                            color="cyan"
                            variant="light"
                          >
                            Support
                          </Badge>
                        )}
                        <Badge
                          color={statusColors[item.status?.toLowerCase()] || "gray"}
                          variant="light"
                        >
                          {item.status}
                        </Badge>
                        {item.primaryKeyword && (
                          <Text size="xs" c="dimmed">
                            Keyword: {item.primaryKeyword}
                          </Text>
                        )}
                      </Group>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconExternalLink size={14} />}
                        component={Link}
                        href={`/content/${item.id}`}
                      >
                        Open in Editor
                      </Button>
                    </Group>

                    <Divider />

                    {/* Article content preview */}
                    <div className="markdown" style={{ maxHeight: 600, overflow: "auto" }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {item.content}
                      </ReactMarkdown>
                    </div>

                    {index < articles.length - 1 && (
                      <Divider size="md" color="blue" />
                    )}
                  </Stack>
                </Paper>
              ))}
          </>
        )}
      </Stack>
    </Container>
  );
}
